import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

export class StadiumLoader {
  constructor() {
    this.loader = new GLTFLoader();
    this.loader.setMeshoptDecoder(MeshoptDecoder);
    this.currentStadium = null;
  }

  /**
   * Loads a GLB/GLTF model from a File object.
   * @param {File} file - The file uploaded by the user
   * @param {THREE.Group} targetGroup - The group where the model should be added
   * @returns {Promise<{baseScale: number, stadiumName: string}>} Bounding info
   */
  loadFromFile(file, targetGroup) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const arrayBuffer = event.target.result;
        
        this.loader.parse(
          arrayBuffer,
          '',
          (gltf) => {
            this._processModel(gltf, targetGroup);
            resolve({
              baseScale: this.baseScale,
              stadiumName: file.name
            });
          },
          (error) => {
            reject(error);
          }
        );
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Loads a GLB/GLTF model from a URL path.
   * @param {string} url - The URL path to fetch the model
   * @param {THREE.Group} targetGroup - The group where the model should be added
   * @returns {Promise<{baseScale: number, stadiumName: string}>} Bounding info
   */
  loadFromUrl(url, targetGroup) {
    return new Promise((resolve, reject) => {
      this.loader.load(
        url,
        (gltf) => {
          this._processModel(gltf, targetGroup);
          const name = url.substring(url.lastIndexOf('/') + 1);
          resolve({
            baseScale: this.baseScale,
            stadiumName: name
          });
        },
        undefined,
        (error) => {
          reject(error);
        }
      );
    });
  }

  /**
   * Internal helper to orient, scale, center and configure shadow attributes.
   */
  _processModel(gltf, targetGroup) {
    // Remove previous model if it exists
    if (this.currentStadium) {
      targetGroup.remove(this.currentStadium);
    }

    this.currentStadium = gltf.scene;

    // Most GLBs are Y-Up. Since our geospatial world is Z-Up, we lay it down correctly.
    this.currentStadium.rotation.x = Math.PI / 2;

    // Configure shadow settings and disable frustum culling to prevent meshes disappearing when panning
    this.currentStadium.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        child.frustumCulled = false; 
        if (child.material) {
          child.material.shadowSide = THREE.DoubleSide;
        }
      }
    });

    // Calculate bounding box for auto-scaling and centering
    const box = new THREE.Box3().setFromObject(this.currentStadium);
    const size = new THREE.Vector3();
    box.getSize(size);

    // Auto-scale target is 300 units for the largest dimension
    this.baseScale = 300 / Math.max(size.x, size.y, size.z);

    const center = new THREE.Vector3();
    box.getCenter(center);

    // Center model relative to origin and sit it exactly on the ground
    this.currentStadium.position.x = -center.x;
    this.currentStadium.position.y = -center.y;
    this.currentStadium.position.z = -box.min.z;

    // Add new stadium model to group
    targetGroup.add(this.currentStadium);
  }
}
